/**
 * WMS Dashboard Widget Registry
 *
 * Slanket ned til 14 referanse-widgets — en per unikt visningsmønster.
 * Fjernede widgets ligger i git-historikk og kan hentes frem ved behov.
 *
 * Alle widgets henter reell data fra DashboardController API.
 *
 * @author BizClick WMS
 * @version 5.0.0
 */

import { WidgetDefinition, WidgetCategory } from './types';

/**
 * All available widgets in the system
 */
export const WIDGET_REGISTRY: WidgetDefinition[] = [
  // === COMPASS (Spør Compass AI-rapporter) - flaggskip ===
  {
    id: 'compass-report',
    name: 'Compass Rapport',
    description: 'Vis en lagret AI-rapport fra Spør Compass',
    category: 'compass',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'wide', 'tall'],
    defaultRefreshInterval: 300,
    icon: 'Compass',
    minWidth: 3,
    minHeight: 2,
    hasBackend: true,
    allowMultiple: true,
  },

  // === ORDERS ===
  {
    id: 'order-flow',
    name: 'Ordreflyt',
    description: 'Traktvisning: Nye - Tildelt - Plukking - Pakket - Sendt',
    category: 'orders',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 60,
    icon: 'GitBranch',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },

  // === PICKING ===
  {
    id: 'remaining-orders',
    name: 'Gjenstaaende oppdrag',
    description: 'Plukkelinjer ikke pabegynt / pagar, gruppert per leveringsmetode',
    category: 'picking',
    defaultSize: 'medium',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 30,
    icon: 'ListTodo',
    minWidth: 3,
    minHeight: 2,
    hasBackend: true,
  },

  // === RECEIVING ===
  {
    id: 'incoming-deliveries',
    name: 'Forventede leveranser',
    description: 'Dagens forventede leveranser med nedtelling til ETA',
    category: 'receiving',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 120,
    icon: 'Truck',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },

  // === INVENTORY ===
  {
    id: 'zone-utilization',
    name: 'Soneutnyttelse',
    description: 'Lagerfyllingsgrad per sone med fargekoding',
    category: 'inventory',
    defaultSize: 'tall',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 300,
    icon: 'MapPin',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },

  // === SHIPPING ===
  {
    id: 'shipping-stats',
    name: 'Utsendingsstatistikk',
    description: 'Oversikt over utsendinger og klar-status',
    category: 'shipping',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'Send',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },

  // === KPI ===
  {
    id: 'sparkline-kpi',
    name: 'KPI med Sparkline',
    description: 'Nokkeltall med mini trendgraf og endringsindikator',
    category: 'kpi',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 60,
    icon: 'TrendingUp',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'animated-counter',
    name: 'Animert teller',
    description: 'Stort animert tall - perfekt for TV-modus',
    category: 'kpi',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'Hash',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },

  // === ACTIVITY ===
  {
    id: 'activity-feed',
    name: 'Aktivitetslogg',
    description: 'Sanntids aktivitetsstrøm fra lageret',
    category: 'activity',
    defaultSize: 'tall',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 10,
    icon: 'Activity',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },
  {
    id: 'mini-heatmap',
    name: 'Sone-heatmap',
    description: 'Rutenett som viser aktivitetsniva per lagersone',
    category: 'activity',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'Grid3X3',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },

  // === OPERATORS ===
  {
    id: 'operator-status',
    name: 'Operatorstatus',
    description: 'Oversikt over operatorer og deres oppgaver',
    category: 'operators',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 30,
    icon: 'Users',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },

  // === GAMIFICATION ===
  {
    id: 'leaderboard',
    name: 'Toppliste',
    description: 'Topp plukkere i dag med podium (gull/solv/bronse)',
    category: 'gamification',
    defaultSize: 'tall',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 60,
    icon: 'Trophy',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },

  // === ALERTS ===
  {
    id: 'bottleneck-detector',
    name: 'Flaskehalsdeteksjon',
    description: 'Identifiserer flaskehalser i lagerprosessene',
    category: 'alerts',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'wide'],
    defaultRefreshInterval: 60,
    icon: 'AlertOctagon',
    minWidth: 3,
    minHeight: 2,
    hasBackend: true,
  },

  // === COMPARISON ===
  {
    id: 'comparison',
    name: 'I dag vs. i gar',
    description: 'Sammenligning av nokkeltall mellom i dag og i gar',
    category: 'comparison',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 60,
    icon: 'ArrowLeftRight',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },
];

/**
 * Get widget definition by ID
 */
export const getWidgetById = (id: string): WidgetDefinition | undefined => {
  return WIDGET_REGISTRY.find((w) => w.id === id);
};

/**
 * Get widgets by category
 */
export const getWidgetsByCategory = (category: WidgetCategory): WidgetDefinition[] => {
  return WIDGET_REGISTRY.filter((w) => w.category === category);
};

/**
 * Get all categories with their widgets
 */
export const getWidgetsByCategories = (): Map<WidgetCategory, WidgetDefinition[]> => {
  const map = new Map<WidgetCategory, WidgetDefinition[]>();

  WIDGET_REGISTRY.forEach((widget) => {
    const existing = map.get(widget.category) || [];
    existing.push(widget);
    map.set(widget.category, existing);
  });

  return map;
};

/**
 * Default layout for new dashboards
 */
export const getDefaultLayout = () => ({
  id: 'default',
  name: 'Standard Dashboard',
  widgets: [
    {
      instanceId: 'inst-1',
      widgetId: 'animated-counter',
      position: { x: 0, y: 0 },
      size: { width: 3, height: 2 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    {
      instanceId: 'inst-2',
      widgetId: 'order-flow',
      position: { x: 3, y: 0 },
      size: { width: 4, height: 4 },
      settings: { refreshInterval: 60, showHeader: true },
    },
    {
      instanceId: 'inst-3',
      widgetId: 'shipping-stats',
      position: { x: 7, y: 0 },
      size: { width: 3, height: 2 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    {
      instanceId: 'inst-4',
      widgetId: 'mini-heatmap',
      position: { x: 10, y: 0 },
      size: { width: 2, height: 2 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    {
      instanceId: 'inst-5',
      widgetId: 'sparkline-kpi',
      position: { x: 0, y: 2 },
      size: { width: 3, height: 2 },
      settings: { refreshInterval: 60, showHeader: true },
    },
    {
      instanceId: 'inst-6',
      widgetId: 'comparison',
      position: { x: 7, y: 2 },
      size: { width: 5, height: 2 },
      settings: { refreshInterval: 60, showHeader: true },
    },
    {
      instanceId: 'inst-7',
      widgetId: 'operator-status',
      position: { x: 0, y: 4 },
      size: { width: 4, height: 4 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    {
      instanceId: 'inst-8',
      widgetId: 'activity-feed',
      position: { x: 4, y: 4 },
      size: { width: 4, height: 4 },
      settings: { refreshInterval: 10, showHeader: true },
    },
    {
      instanceId: 'inst-9',
      widgetId: 'leaderboard',
      position: { x: 8, y: 4 },
      size: { width: 4, height: 4 },
      settings: { refreshInterval: 60, showHeader: true },
    },
  ],
  gridColumns: 12,
  gridRows: 8,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  isDefault: true,
});
